import React, { useRef, useEffect, useState } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const EarthingForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',

      // Level 1
      visual_checks: Array(12).fill({ status: '', remark: '' }),
      physical_checks: Array(2).fill({ status: '', remark: '' }),
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_other_comments: '',
      l1_status: 'draft',

      // Level 2
      earth_pits_healthy: '',
      welding_ok: '',
      testing_commissioning: '',
      earthing_resistance_test: '',
      instruments: [{ serial: '', range: '', calibration: '' }],
      earthing_tests: Array(25).fill({ pit_no: '', purpose: '', value: '' }),
      observations: { a: '', b: '', c: '' },
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_other_comments: '',
      l2_status: 'draft',

      // parity with APFC/DBPanel
      manager_approve: false,
    },
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });
  const l1_manager_id = useWatch({ control, name: 'l1_manager_id' });
  const l2_manager_id = useWatch({ control, name: 'l2_manager_id' });

  // ---------- Autosave with computed common status ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const l1_status = data.l1_status;
      const l2_status = data.l2_status;
      let common_status = 'draft';
      if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') common_status = 'CONTRACTOR';
      else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') common_status = 'MANAGER';
      else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') common_status = 'QUALITY';
      else if (l1_status === 'approved' && l2_status === 'approved') common_status = 'approved';

      const payload = {
        form_data: {
          status: common_status,
          form_id: formId,
          formName: 'earthing',
          station_id: data.station_id,
          project_name: data.project_name,
          manager_approve: data.manager_approve,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            other_comments: data.l1_other_comments,
            status: l1_status,
          },
          l2: {
            earth_pits_healthy: data.earth_pits_healthy,
            welding_ok: data.welding_ok,
            testing_commissioning: data.testing_commissioning,
            earthing_resistance_test: data.earthing_resistance_test,
            instruments: data.instruments,
            earthing_tests: data.earthing_tests,
            observations: data.observations,
            comments: data.l2_comments,
            other_comments: data.l2_other_comments,
            status: l2_status,
          },
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) dispatch({ type: VIEW_FORM, payload: { formId, formName: 'earthing' } });
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('station_id', d.station_id || stationId || '');
      setValue('project_name', d.project_name || '');

      setValue('visual_checks', Array.isArray(d.l1?.visual_checks) ? d.l1.visual_checks : Array(12).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(d.l1?.physical_checks) ? d.l1.physical_checks : Array(2).fill({ status: '', remark: '' }));
      setValue('l1_comments', d.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_other_comments', d.l1?.other_comments || '');
      setValue('l1_status', d.l1?.status || 'draft');
      setValue('l1_manager_id', d.l1?.managerId || null);

      setValue('earth_pits_healthy', d.l2?.earth_pits_healthy || '');
      setValue('welding_ok', d.l2?.welding_ok || '');
      setValue('testing_commissioning', d.l2?.testing_commissioning || '');
      setValue('earthing_resistance_test', d.l2?.earthing_resistance_test || '');
      setValue('instruments', Array.isArray(d.l2?.instruments) ? d.l2.instruments : [{ serial: '', range: '', calibration: '' }]);
      setValue('earthing_tests', Array.isArray(d.l2?.earthing_tests) ? d.l2.earthing_tests : Array(25).fill({ pit_no: '', purpose: '', value: '' }));
      setValue('observations', d.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_comments', d.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_other_comments', d.l2?.other_comments || '');
      setValue('l2_status', d.l2?.status || 'draft');
      setValue('l2_manager_id', d.l2?.managerId || null);

      setValue('manager_approve', d.manager_approve || false);
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue, role]);

  // ---------- Role/level gating ----------
  const isLevel1Editable = !l1_manager_id && ((role === 'engineer' && (l1_status === 'draft' || l1_status === 'CONTRACTOR' || l1_status === 'MANAGER')) || (role === 'contractor' && (l1_status === 'draft' || l1_status === 'CONTRACTOR')));
  const isLevel2Editable = !l2_manager_id && ((role === 'engineer' && (l2_status === 'draft' || l2_status === 'CONTRACTOR' || l2_status === 'MANAGER')) || (role === 'contractor' && (l2_status === 'draft' || l2_status === 'CONTRACTOR')));

  const canCommentLevel1 =
    (role === 'engineer' && (getValues('l1_status') === 'draft' || getValues('l1_status') === 'CONTRACTOR')) ||
    (role === 'contractor' && getValues('l1_status') === 'CONTRACTOR') ||
    (role === 'manager' && getValues('l1_status') === 'MANAGER') ||
    (role === 'quality' && getValues('l1_status') === 'QUALITY');

  const canCommentLevel2 =
    (role === 'engineer' && (getValues('l2_status') === 'draft' || getValues('l2_status') === 'CONTRACTOR')) ||
    (role === 'contractor' && getValues('l2_status') === 'CONTRACTOR') ||
    (role === 'manager' && getValues('l2_status') === 'MANAGER') ||
    (role === 'quality' && getValues('l2_status') === 'QUALITY');

  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR';

  const isSigEditable = (level, signatureRole) => {
    const managerId = level === 'l1' ? getValues('l1_manager_id') : getValues('l2_manager_id');
    if (managerId) return false;
    const s = getValues(`${level}_status`);
    return (
      (role === 'engineer' && signatureRole === 'charge_engineer' && (s === 'draft' || s === 'CONTRACTOR' || s === 'MANAGER')) ||
      (role === 'contractor' && signatureRole === 'contractor' && (s === 'draft' || s === 'CONTRACTOR')) ||
      (role === 'quality' && signatureRole === 'quality' && s === 'QUALITY')
    );
  };

  // ---------- Signature handlers ----------
  const handleImageUpload = (e, level, sigRole) => {
    if (!isSigEditable(level, sigRole)) return;
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    if (!isSigEditable(level, sigRole)) return;
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    if (!isSigEditable(level, sigRole)) return;
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    const { level, role: sigRole } = currentSignature;
    if (!isSigEditable(level, sigRole)) { setShowModal(false); return; }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${level}_comments.${sigRole}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => { sigCanvas.current?.clear(); };
  const handleCloseModal = () => { setShowModal(false); sigCanvas.current?.clear(); };

  // ---------- Submit handlers (APFC-like transitions) ----------
  const onSubmit = (level, status) => (data) => {
    // allow only at correct stage
    if (role === 'engineer' && getValues(`${level}_status`) !== 'draft' && getValues(`${level}_status`) !== 'CONTRACTOR') return;
    if (role === 'contractor' && getValues(`${level}_status`) !== 'draft' && getValues(`${level}_status`) !== 'CONTRACTOR') return;

    let l1_status = data.l1_status;
    let l2_status = data.l2_status;
    let manager_approve = data.manager_approve;

    if (level === 'l1') l1_status = status;
    if (level === 'l2') l2_status = status;

    if (role === 'engineer') {
      if (level === 'l1') l1_status = 'CONTRACTOR';
      if (level === 'l2') l2_status = 'CONTRACTOR';
    } else if (role === 'contractor') {
      if (level === 'l1') l1_status = 'MANAGER';
      if (level === 'l2') l2_status = 'MANAGER';
    } else if (role === 'manager' && status === 'approved') {
      if (level === 'l1') l1_status = 'QUALITY';
      if (level === 'l2') l2_status = 'QUALITY';
      manager_approve = true;
    } else if (role === 'manager' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    } else if (role === 'quality' && status === 'approved') {
      if (level === 'l1') l1_status = 'approved';
      if (level === 'l2') l2_status = 'approved';
    } else if (role === 'quality' && status === 'draft') {
      if (level === 'l1') l1_status = 'draft';
      if (level === 'l2') l2_status = 'draft';
    }

    // compute common status
    let common_status = 'draft';
    if (l1_status === 'CONTRACTOR' && l2_status === 'CONTRACTOR') common_status = 'CONTRACTOR';
    else if (l1_status === 'MANAGER' && l2_status === 'MANAGER') common_status = 'MANAGER';
    else if (l1_status === 'QUALITY' && l2_status === 'QUALITY') common_status = 'QUALITY';
    else if (l1_status === 'approved' && l2_status === 'approved') common_status = 'approved';

    const payload = {
      form_data: {
        status: common_status,
        form_id: formId,
        formName: 'earthing',
        station_id: data.station_id,
        project_name: data.project_name,
        manager_approve,

        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          other_comments: data.l1_other_comments,
          status: l1_status,
        },
        l2: {
          earth_pits_healthy: data.earth_pits_healthy,
          welding_ok: data.welding_ok,
          testing_commissioning: data.testing_commissioning,
          earthing_resistance_test: data.earthing_resistance_test,
          instruments: data.instruments,
          earthing_tests: data.earthing_tests,
          observations: data.observations,
          comments: data.l2_comments,
          other_comments: data.l2_other_comments,
          status: l2_status,
        },
        punch_points: data.punch_points,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(
      `Level ${level.toUpperCase()} ${
        status === 'pending' || status === 'approved' ? 'Submitted' : 'Saved as Draft'
      } Successfully`
    );
    navigate(-1);
  };

    const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      if (pendingRework.level) {
        handleSubmit(onSubmit(pendingRework.level, pendingRework.action))();
      } else {
        handleSubmit(onSubmit(pendingRework.action))();
      }
    }
  };

if (isLoading) return <div>Loading...</div>;

  // ---------- Signature cell ----------
  const renderSignatureBlock = (level, signatureRole, field) => {
    const editable = isSigEditable(level, signatureRole);

    if (field === 'Date') {
      return <Form.Control type="date" {...register(`${level}_comments.${signatureRole}.date`)} disabled={!editable} />;
    }
    if (field === 'Signature') {
      return (
        <div>
          <Form.Select
            {...register(`${level}_comments.${signatureRole}.signatureType`)}
            onChange={(e) => handleSignatureTypeChange(level, signatureRole, e.target.value)}
            className="mb-2"
            disabled={!editable}
          >
            <option value="upload">Image Upload</option>
            <option value="digital">Digital Sign</option>
          </Form.Select>

          {_.get(formValues, `${level}_comments.${signatureRole}.signatureType`) === 'upload' && (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, level, signatureRole)}
              className="mb-2"
              disabled={!editable}
            />
          )}

          {_.get(formValues, `${level}_comments.${signatureRole}.signature`) && (
            <div>
              <img
                src={_.get(formValues, `${level}_comments.${signatureRole}.signature`)}
                alt="Signature"
                style={{ maxWidth: '100px', marginTop: '5px' }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleResetSignature(level, signatureRole)}
                className="mt-2"
                disabled={!editable}
              >
                Reset Signature
              </Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <Form.Control
        type="text"
        {...register(`${level}_comments.${signatureRole}.${field.toLowerCase()}`)}
        disabled={!editable}
      />
    );
  };

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-4">Earthing - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      {(!isLevel1Editable && !isLevel2Editable && role === 'engineer' && l1_status !== 'draft' && l1_status !== 'CONTRACTOR' && l2_status !== 'draft' && l2_status !== 'CONTRACTOR') && (
        <div className="alert alert-warning mb-4">This form is in View Mode Only for Engineer.</div>
      )}

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!(isLevel1Editable || isLevel2Editable)} />
            </Form.Group>
          </Col>
        </Row>

        
        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title="Level 1 - Pre-Commissioning" disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Is the earthing installation in accordance with IS 3043, the approved earthing drawing, and specifications?',
                  'Is all earthing material as per the design parameters and specifications?',
                  'Does the size and rating of the earthing conductor comply with the specifications and standards?',
                  'Has the Earth Material TC been checked?',
                  'Is the painting of joints in Copper and GI strips done as per requirements?',
                  'Is the main earth loop laid as per IS 3043 at a depth of 600 mm below grade level?',
                  'Is the earthing pit covered with RCC/Cast Iron/HDPE cover as indicated in the drawing?',
                  'Are the various earth connections tightened properly, and are the bolts of correct size and quality?',
                  'Is the instruction board of the earth pit installed on the pit cover?',
                  'Are all instrumentation/electronic devices earthed to a separate earth pit (electronics earth pit)?',
                  'Is disconnection provided for resistance checks and is the pit chamber size adequate?',
                  'Is the clean earth insulated and connected to a separate earth electrode where applicable?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`visual_checks_${index}`} value="Yes" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No"  type="radio" name={`visual_checks_${index}`} value="No"  {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA"  type="radio" name={`visual_checks_${index}`} value="NA"  {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td><Form.Control type="text" {...register(`visual_checks.${index}.remark`)} disabled={!isLevel1Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
              </thead>
              <tbody>
                {[
                  'Is the earth strip/cable properly tightened with the earth and equipment?',
                  'Are the correct methods used for cable termination and tagging?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`physical_checks_${index}`} value="Yes" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No"  type="radio" name={`physical_checks_${index}`} value="No"  {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA"  type="radio" name={`physical_checks_${index}`} value="NA"  {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td><Form.Control type="text" {...register(`physical_checks.${index}.remark`)} disabled={!isLevel1Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                      <td key={sigRole}>{renderSignatureBlock('l1', sigRole, field)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">5. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l1_other_comments')}
                disabled={!canCommentLevel1}
                placeholder="Enter any additional comments or remarks for Level 1"
              />
            </Form.Group>

            {/* Level 1 Actions */}
            {role === 'manager' && getValues('l1_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Approve Level 1
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Re-Work Level 1
                </Button>
              </div>
            ) : role === 'quality' && getValues('l1_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Approve Level 1
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Apply for Resubmit Level 1
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l1', 'pending'))}
                  disabled={role === 'engineer' ? (getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR') : role === 'contractor' ? getValues('l1_status') !== 'CONTRACTOR' : true}
                >
                  Submit Level 1
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleReworkClick('l1', 'draft')}
                  disabled={role === 'engineer' ? (getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR') : role === 'contractor' ? getValues('l1_status') !== 'CONTRACTOR' : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          <Tab eventKey="level2" title="Level 2 - Commissioning" disabled={disableLevel2Tab}>
            <h3 className="mt-4">6. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>All Earth Pits Found Healthy & Connected to System</Form.Label>
                  <Form.Select {...register('earth_pits_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Ensure All Weldings Are OK</Form.Label>
                  <Form.Select {...register('welding_ok')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Earthing Resistance Test</Form.Label>
                  <Form.Select {...register('earthing_resistance_test')} disabled={!isLevel2Editable}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">7. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Instrument Description</th><th>Instrument Serial No.</th><th>Range</th><th>Calibration Status</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Earth Tester</td>
                  <td><Form.Control type="text" {...register('instruments.0.serial')} disabled={!isLevel2Editable} /></td>
                  <td><Form.Control type="text" {...register('instruments.0.range')}  disabled={!isLevel2Editable} /></td>
                  <td><Form.Control type="text" {...register('instruments.0.calibration')} disabled={!isLevel2Editable} /></td>
                </tr>
              </tbody>
            </Table>

            <h3 className="mt-4">8. Earthing Resistance Test Max. value less than 5Ω</h3>
            <Table bordered>
              <thead>
                <tr><th>Sr. No.</th><th>Pit No.</th><th>Purpose</th><th>Value (Ω)</th></tr>
              </thead>
              <tbody>
                {Array.from({ length: 25 }, (_, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        defaultValue={`EP-${index + 1}`}
                        {...register(`earthing_tests.${index}.pit_no`)}
                        disabled={!isLevel2Editable}
                      />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`earthing_tests.${index}.purpose`)} disabled={!isLevel2Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`earthing_tests.${index}.value`)} disabled={!isLevel2Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">9. Observations during Commissioning / Testing</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control type="text" {...register(`observations.${key}`)} disabled={!isLevel2Editable} />
              </Form.Group>
            ))}

            <h3 className="mt-4">10. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                      <td key={sigRole}>{renderSignatureBlock('l2', sigRole, field)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">11. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_other_comments')}
                disabled={!canCommentLevel2}
                placeholder="Enter any additional comments or remarks for Level 2"
              />
            </Form.Group>

            {/* Level 2 Actions */}
            {role === 'manager' && getValues('l2_status') === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Approve Level 2
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Re-Work Level 2
                </Button>
              </div>
            ) : role === 'quality' && getValues('l2_status') === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Approve Level 2
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Apply for Resubmit Level 2
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleSubmit(onSubmit('l2', 'pending'))}
                  disabled={role === 'engineer' ? (getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR') : role === 'contractor' ? getValues('l2_status') !== 'CONTRACTOR' : true}
                >
                  Submit Level 2
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleReworkClick('l2', 'draft')}
                  disabled={role === 'engineer' ? (getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR') : role === 'contractor' ? getValues('l2_status') !== 'CONTRACTOR' : true}
                >
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        {/* Signature Modal */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    
      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
</div>
  );
};

EarthingForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
};

export default EarthingForm;
